<script setup>
import {computed, getCurrentInstance, onMounted, reactive, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import LoadingIcon from "@/components/LoadingIcon.vue";
import Paginate from "vuejs-paginate-next"
import { accessControlApi, connectionsApi } from "@/api/forwriting_index";
import {datePipe} from "@/utils/datatimeUtils";
import {userApi} from "@/api";
import CheckPointLimit from "@/views/forwriting/fw00_check_point_limit.vue"

const instance = getCurrentInstance()
const t = instance.proxy.$t

const route = useRoute()
const router = useRouter();
const article_list = reactive({
  id: 0
})

// 初始化時設置 article_list_id
onMounted(() => {
  const article_list_id = parseInt(route.params.article_list_id)
  if (article_list_id && !isNaN(article_list_id) && article_list_id > 0) {
    article_list.id = article_list_id
    console.log("article_list.id 已設置為:", article_list.id)
  } else {
    console.error("無法從路由參數獲取有效的 article_list_id:", route.params.article_list_id)
    alert("錯誤：無法獲取有效的 article_list_id，請檢查 URL 是否正確")
  }
})

// 監聽路由變化，更新 article_list.id
watch(() => route.params.article_list_id, (newId) => {
  const article_list_id = parseInt(newId)
  if (article_list_id && !isNaN(article_list_id) && article_list_id > 0) {
    article_list.id = article_list_id
    console.log("article_list.id 已更新為:", article_list.id)
    reload_data()
  }
})

const authentication_types = [
  "Basic Authentication",
  "JWT Authentication",
  "OAuth 2.0 Authentication",
  "Application Passwords Authentication"
]

// ==== modal_new ====

const modal_new = reactive({
  show: false,
  connection: {
    article_list_id: 0,
    connection_name: "",
    integration: "wordpress",
    authentication_type: "Basic Authentication",
    domain_url: "",

    username: "",
    password: "",

    client_id: "",
    client_secret: "",

    application_password: "",
  },
})
// authentication_type_changed
const authentication_type_changed = async () => {
  console.log( "authentication_type:", modal_new.connection.authentication_type)
}

// integration_changed
const integration_changed = async () => {
  console.log( "integration:", modal_new.connection.integration)
  // Notion を選択した場合、自動的に Application Passwords Authentication に設定（Internal Integration Token を使用）
  if( modal_new.connection.integration === "notion") {
    modal_new.connection.authentication_type = "Application Passwords Authentication"
  }
  // MicroAd CMS の場合，不需要特定的 authentication_type，但可以設置為 Basic Authentication
  if( modal_new.connection.integration === "microad_cms") {
    // MicroAd CMS 使用 Selenium 自動化，不需要傳統的認證方式
    // 但為了保持一致性，可以設置為 Basic Authentication（實際上不會使用）
    modal_new.connection.authentication_type = "Basic Authentication"
  }
}
const modal_new_open = async () => {
  modal_new.connection.connection_name = ""
  modal_new.connection.integration = "wordpress"
  modal_new.connection.authentication_type = "Basic Authentication"
  modal_new.connection.domain_url = ""

  modal_new.connection.username = ""
  modal_new.connection.password = ""

  modal_new.connection.client_id = ""
  modal_new.connection.client_secret = ""

  modal_new.connection.application_password = ""

  modal_new.show = true
}
const modal_new_create = async () => {
  isLoading.show = true;
  console.log( "connection:", modal_new.connection)
  console.log( "article_list.id:", article_list.id)
  
  // 驗證 article_list_id
  if (!article_list.id || article_list.id === 0) {
    isLoading.show = false;
    alert("錯誤：無法獲取 article_list_id，請刷新頁面重試")
    throw new Error("article_list_id is invalid")
  }
  
  // 驗證必需字段
  if (!modal_new.connection.connection_name || modal_new.connection.connection_name.trim() === "") {
    isLoading.show = false;
    alert("請輸入連接名稱")
    return
  }
  if (!modal_new.connection.domain_url || modal_new.connection.domain_url.trim() === "") {
    isLoading.show = false;
    alert("請輸入基本URL (domain_url)")
    return
  }
  
  try {
    const resp = await connectionsApi.new(
        article_list.id, modal_new.connection)
    if( resp.status !== 200) {
      isLoading.show = false;
      console.error("API 錯誤:", resp)
      alert(`創建連接失敗：${resp.data?.detail || resp.statusText}`)
      throw new Error("ERROR");
    }
    await reload_data()
    isLoading.show = false;
    modal_new.show = false;
  } catch (error) {
    isLoading.show = false;
    console.error("創建連接時發生錯誤:", error)
    if (error.response) {
      alert(`創建連接失敗：${error.response.data?.detail || error.response.statusText}`)
    } else {
      alert(`創建連接失敗：${error.message}`)
    }
    throw error
  }
}

// ==== modal_update ====

const modal_update = reactive({
  show: false,
  connection: { },
  id: -1
})
const modal_update_open = async( par_id) => {
  console.log( "par_id:", par_id)
  modal_update.id = par_id

  const resp = await connectionsApi.get_1( par_id)
  console.log( "resp:", resp.data)

  modal_update.connection
      = resp.data

  // modal_update.sample_article.sample_article_content
  //     = resp.data.sample_article_content

  modal_update.show = true
}
const modal_update_confirm = async ( ) => {
  isLoading.show = true;

  const resp = await connectionsApi.update(
      modal_update.id, modal_update.connection)
  if( resp.status !== 200) {
    isLoading.show = false;
    throw new Error("ERROR");
  }

  await reload_data()
  isLoading.show = false;

  modal_update.show = false
}

// ==== modal_del_1 ====

const modal_del_1 = reactive({
  show: false,
  msg: "",
  id: -1,
})
const handleDelete = async ( par_id, connection_name) => {
  modal_del_1.msg = t('connections.delete_prompt'
  ).replace( "name", connection_name)
  modal_del_1.id = par_id
  modal_del_1.show = true;
};
const handleDelete_confirm = async () => {
  isLoading.show = true;

  try {
    const resp = await connectionsApi.delete( modal_del_1.id );
    if( resp.status !== 200) {
      isLoading.show = false;
      throw new Error("ERROR");
    }

    data.items = data.items.filter( ( item) => item.id !== modal_del_1.id);

    isLoading.show = false;
    modal_del_1.show = false;
  } catch (error) {
    isLoading.show = false;
    console.error("刪除連接失敗:", error);
    console.error("錯誤詳情:", {
      message: error.message,
      response: error.response,
      config: error.config
    });
    
    // 提取後端返回的友好錯誤消息（支持日文|中文格式）
    let errorMessage = 'Network Error';
    if (error.response && error.response.data) {
      if (error.response.data.detail) {
        const detail = error.response.data.detail;
        // 檢查是否為「日文|中文」格式
        if (detail.includes('|')) {
          const [jaMsg, twMsg] = detail.split('|');
          // 根據當前語言選擇對應的訊息
          const currentLocale = instance.proxy.$i18n.locale.value || 'ja';
          errorMessage = currentLocale === 'tw' ? twMsg.trim() : jaMsg.trim();
        } else {
          errorMessage = detail;
        }
      } else if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    alert(`刪除失敗: ${errorMessage}`);
  }
};

// ==== modal_del_selected ====

const modal_del_selected = reactive({
  show: false,
  msg: "",
  ids: [],
})
const del_selected = async () => {
  modal_del_selected.ids = []
  for (const i in checked.items)
    if( checked.items[i])
      modal_del_selected.ids.push( i)

  modal_del_selected.msg = t('connections.delete_prompt_much'
  ).replace( "num", modal_del_selected.ids.length)
  modal_del_selected.show = true
}
const del_selected_confirm = async () => {
  isLoading.show = true
  for( const i in modal_del_selected.ids)
  {
    const id = modal_del_selected.ids[i]
    console.log( "*** id:", id)
    const resp = await connectionsApi.delete( id );
    if( resp.status !== 200) {
      isLoading.show = false;
      throw new Error(`connectionsApi.delete() ERROR`)
    }
  }
  await reload_data()
  isLoading.show = false

  modal_del_selected.show = false
}

// ==== filter ====

const filter = reactive({
  temp: "",
  searchKey: null,
});
const handleFilter = () => {
  filter.searchKey = filter.temp
  page.current = 1
};
const filteredData = computed(() => {
  let temp = data.items || [];
  if( filter.searchKey)
  {
    const str_filter = filter.searchKey.toLowerCase()
    temp = temp.filter( ( item) =>
        String( item.connection_name).toLowerCase().indexOf( str_filter) > -1
    )
  }
  return temp;
});

// ==== paging ====

const page = reactive({
  current: 1,
  num: 10,
})
const page_count = computed(() => {
  let cc = Math.floor( filteredData.value.length / page.num)
  if( filteredData.value.length % page.num === 0)
    return cc
  return cc + 1
})

// ==== init data... ====

const data = reactive({
  items: [],
  public_id: 1,
  tenant_id: null,
  can_use_microad_cms: false  // 只有 tenant_id 1 和 96 可以使用 MicroAd CMS
})
const reload_data = async( ) => {
  if (!article_list.id || article_list.id === 0) {
    console.warn("article_list.id 無效，跳過 reload_data:", article_list.id)
    return
  }
  const resp = await connectionsApi.getAll_filter( article_list.id)
  data.items = resp.data;

  const user = await userApi.whoami();
  const tenant_id = user.data.tenant_id
  data.tenant_id = tenant_id
  console.log( "tenant_id:", tenant_id)
  if( tenant_id===1){
    data.public_id = 0
  }
  
  // 檢查是否可以使用 MicroAd CMS（只有 tenant_id 1 和 96）
  data.can_use_microad_cms = (tenant_id === 1 || tenant_id === 96)

  for( let i in data.items) {
    const article_list_id = data.items[i].article_list_id
    if( article_list_id !== data.public_id) {
      const id = data.items[i].id
      checked.items[id] = false
    }
  }
}
onMounted(async () => {
  if( ! await accessControlApi.check_option_access()) {
    await router.push(`/forwriting/options_error`)
    return
  }

  await reload_data()

  // check the point_limit
  point_limit.is_over = await ref_CheckPointLimit.value.check()

  // todo: for testing
  // modal_new.show = true
});

// ==== checked/un-checked ====

const checked = reactive({
  items: {}
})
const onSelectAllChange = async( e) => {
  const checked_val = e.target.checked
  for( const i in checked.items)
    checked.items[i] = checked_val
  // console.log( "checked_items:", checked.items)
}

// ==== button refresh ====

const refreshPage = async () => {
  await reload_data()
  window.location.reload();
}

// ==== isLoading ====

const isLoading = reactive({
  show: false
})

// ==== modal_over_points ====
const ref_CheckPointLimit = ref(null)
const point_limit = reactive({
  is_over: false,
})

</script>

<template>

  <!-- isLoading -->
  <LoadingIcon :is-loading="isLoading.show"></LoadingIcon>

  <!-- modal_over_points -->
  <CheckPointLimit ref="ref_CheckPointLimit"></CheckPointLimit>

  <!-- Modal_new" -->
  <div v-if="modal_new.show" class="modal-create">
    <div class="modal-content">

      <div class="row" >
        <h4>{{ $t( "connections.new_connection") }}</h4>
      </div>

      <div class="row" style="margin-top: 30px">
        <label>{{ $t( "connections.connection_name") }}:</label>
        <input type="text" style="margin-left: 10px; width: 800px"
               v-model="modal_new.connection.connection_name"/>
      </div>

      <!-- integration -->
      <div class="row" style="margin-top: 30px">
        <label>接続先 (Integration):</label>
        <select class="form-select td-combo"
                v-model="modal_new.connection.integration"
                @change="integration_changed">
          <option value="wordpress">WordPress</option>
          <option value="notion">Notion</option>
          <option value="microad_cms" :disabled="!data.can_use_microad_cms">MicroAd CMS</option>
        </select>
        <div v-if="!data.can_use_microad_cms" style="margin-top: 5px; font-size: 12px; color: #999;">
          ※ MicroAd CMS は特定のテナント（ID: 1, 96）のみ利用可能です
        </div>
      </div>
      
      <!-- Notion 説明 -->
      <div v-if="modal_new.connection.integration === 'notion'" 
           class="row" style="margin-top: 10px; padding: 10px; background-color: #f0f0f0; border-radius: 5px;">
        <div style="font-size: 12px; color: #666;">
          <strong>Notion 設定説明（Internal Integration Token を使用）：</strong><br>
          • <strong>接続名:</strong> カスタム名（例：Notion_test_Erick）<br>
          • <strong>基本URL (domain_url):</strong> Notion Database ID を入力（32 文字、例：2a4d169ac66980b7b9d0e085aa715b80）<br>
          • <strong>アプリケーションパスワード (application_password):</strong> Notion Integration Token を入力（形式：secret_xxx または ntn_xxx）<br>
          • <strong>注意：</strong> Notion では「ユーザー名」フィールドの入力は不要です<br>
          • <strong>取得方法：</strong><br>
          &nbsp;&nbsp;1. <a href="https://www.notion.com/my-integrations" target="_blank">Notion Integrations</a> にアクセス<br>
          &nbsp;&nbsp;2. "+ New integration" をクリックして Internal Integration を作成<br>
          &nbsp;&nbsp;3. Integration Token をコピー<br>
          &nbsp;&nbsp;4. Notion Database でこの Integration を接続（右上の "..." → "Connections"）
        </div>
      </div>
      
      <!-- MicroAd CMS 説明 -->
      <div v-if="modal_new.connection.integration === 'microad_cms'" 
           class="row" style="margin-top: 10px; padding: 10px; background-color: #fff3cd; border-radius: 5px; border: 1px solid #ffc107;">
        <div style="font-size: 12px; color: #856404;">
          <strong>MicroAd CMS 設定説明：</strong><br>
          • <strong>接続名:</strong> カスタム名（例：japaholic_tw）<br>
          • <strong>基本URL (domain_url):</strong> MicroAd CMS の管理画面 URL（例：https://www.japaholic.com/guanli）<br>
          • <strong>注意：</strong> MicroAd CMS は Selenium を使用して自動化されるため、認証情報は不要です<br>
          • <strong>権限：</strong> この機能は特定のテナント（ID: 1, 96）のみ利用可能です<br>
          • <strong>機能：</strong> 記事を MicroAd CMS に自動投稿します
        </div>
      </div>

<!--      authentication_type-->
      <div class="row" style="margin-top: 30px">
        <label>{{ $t( "connections.authentication_type") }}:</label>
        <select class="form-select td-combo"
                v-model="modal_new.connection.authentication_type"
                @change="authentication_type_changed">
          <!-- Notion の場合、Application Passwords Authentication のみ表示 -->
          <template v-if="modal_new.connection.integration === 'notion'">
            <option>{{authentication_types[3]}}</option>
          </template>
          <template v-else>
            <option>{{authentication_types[0]}}</option>
            <option>{{authentication_types[1]}}</option>
            <option>{{authentication_types[2]}}</option>
            <option>{{authentication_types[3]}}</option>
          </template>
        </select>
      </div>

      <!-- domain_url: 0,1,2,3 -->
      <div class="row" style="margin-top: 30px">
        <label>{{ $t( "connections.domain_url") }}<span v-if="modal_new.connection.integration === 'notion'"> (Notion Database ID)</span>:</label>
        <input type="text" style="margin-left: 10px; width: 800px"
               v-model="modal_new.connection.domain_url"
               :placeholder="modal_new.connection.integration === 'notion' ? '例: 2a4d169ac66980b7b9d0e085aa715b80' : ''"/>
      </div>

      <!-- username: 0,1,3 (ただし Notion では username は不要) -->
      <div v-show="[
          authentication_types[0],
          authentication_types[1],
          authentication_types[3]
          ].includes( modal_new.connection.authentication_type) && modal_new.connection.integration !== 'notion'"
          class="row" style="margin-top: 30px">
        <label>{{ $t( "connections.username") }}:</label>
        <input type="text" style="margin-left: 10px; width: 800px"
               v-model="modal_new.connection.username"/>
      </div>

      <!-- password: 0,1 -->
      <div v-show="[
          authentication_types[0],
          authentication_types[1]
           ].includes( modal_new.connection.authentication_type)"
          class="row" style="margin-top: 30px">
        <label>{{ $t( "connections.password") }}:</label>
        <input type="text" style="margin-left: 10px; width: 800px"
               v-model="modal_new.connection.password"/>
      </div>

      <!-- client_id: 2 -->
      <div v-show="[
          authentication_types[2]
           ].includes( modal_new.connection.authentication_type)"
          class="row" style="margin-top: 30px">
        <label>Client ID:</label>
        <input type="text" style="margin-left: 10px; width: 800px"
               v-model="modal_new.connection.client_id"/>
      </div>

      <!-- client_secret: 2 -->
      <div v-show="[
          authentication_types[2]
           ].includes( modal_new.connection.authentication_type)"
          class="row" style="margin-top: 30px">
        <label>client secret:</label>
        <input type="text" style="margin-left: 10px; width: 800px"
               v-model="modal_new.connection.client_secret"/>
      </div>

      <!-- application_password: 3 -->
      <div v-show="[
          authentication_types[3]
           ].includes( modal_new.connection.authentication_type)"
          class="row" style="margin-top: 30px">
        <label>{{ $t( "connections.application_password") }}<span v-if="modal_new.connection.integration === 'notion'"> (Notion Integration Token)</span>:</label>
        <input type="text" style="margin-left: 10px; width: 800px"
               v-model="modal_new.connection.application_password"
               :placeholder="modal_new.connection.integration === 'notion' ? '例: secret_xxx または ntn_xxx' : ''"/>
      </div>

      <!-- 2 buttons-->
      <div class="row" style="margin-top: 20px">
        <button class="btn btn-primary"
                style="width: 120px; margin-left: 10px"
                @click="modal_new_create">
          {{ $t( "connections.append_2") }}</button>

        <button class="btn btn-primary" style="width: 120px; margin-left: 20px"
                @click="modal_new.show=false">
          {{ $t( "connections.return") }}</button>
      </div>

    </div>
  </div>
  <!-- end of Modal_new" -->

  <!-- Modal_update" -->
  <div v-if="modal_update.show" class="modal-create">
    <div class="modal-content">

      <div class="row" >
        <h4>{{ $t( "connections.edit_connection") }}</h4>
      </div>

      <!-- connection_name-->
      <div class="row" style="margin-top: 30px">
        <label>{{ $t( "connections.connection_name") }}:</label>
        <input type="text" style="margin-left: 10px; width: 800px"
               v-model="modal_update.connection.connection_name"/>
      </div>

      <!-- integration -->
      <div class="row" style="margin-top: 30px">
        <label>接続先 (Integration):</label>
        <select class="form-select td-combo"
                v-model="modal_update.connection.integration"
                @change="integration_changed">
          <option value="wordpress">WordPress</option>
          <option value="notion">Notion</option>
          <option value="microad_cms" :disabled="!data.can_use_microad_cms">MicroAd CMS</option>
        </select>
        <div v-if="!data.can_use_microad_cms" style="margin-top: 5px; font-size: 12px; color: #999;">
          ※ MicroAd CMS は特定のテナント（ID: 1, 96）のみ利用可能です
        </div>
      </div>
      
      <!-- Notion 説明 -->
      <div v-if="modal_update.connection.integration === 'notion'" 
           class="row" style="margin-top: 10px; padding: 10px; background-color: #f0f0f0; border-radius: 5px;">
        <div style="font-size: 12px; color: #666;">
          <strong>Notion 設定説明（Internal Integration Token を使用）：</strong><br>
          • <strong>接続名:</strong> カスタム名（例：Notion_test_Erick）<br>
          • <strong>基本URL (domain_url):</strong> Notion Database ID を入力（32 文字、例：2a4d169ac66980b7b9d0e085aa715b80）<br>
          • <strong>アプリケーションパスワード (application_password):</strong> Notion Integration Token を入力（形式：secret_xxx または ntn_xxx）<br>
          • <strong>注意：</strong> Notion では「ユーザー名」フィールドの入力は不要です<br>
          • <strong>取得方法：</strong><br>
          &nbsp;&nbsp;1. <a href="https://www.notion.com/my-integrations" target="_blank">Notion Integrations</a> にアクセス<br>
          &nbsp;&nbsp;2. "+ New integration" をクリックして Internal Integration を作成<br>
          &nbsp;&nbsp;3. Integration Token をコピー<br>
          &nbsp;&nbsp;4. Notion Database でこの Integration を接続（右上の "..." → "Connections"）
        </div>
      </div>
      
      <!-- MicroAd CMS 説明 -->
      <div v-if="modal_update.connection.integration === 'microad_cms'" 
           class="row" style="margin-top: 10px; padding: 10px; background-color: #fff3cd; border-radius: 5px; border: 1px solid #ffc107;">
        <div style="font-size: 12px; color: #856404;">
          <strong>MicroAd CMS 設定説明：</strong><br>
          • <strong>接続名:</strong> カスタム名（例：japaholic_tw）<br>
          • <strong>基本URL (domain_url):</strong> MicroAd CMS の管理画面 URL（例：https://www.japaholic.com/guanli）<br>
          • <strong>注意：</strong> MicroAd CMS は Selenium を使用して自動化されるため、認証情報は不要です<br>
          • <strong>権限：</strong> この機能は特定のテナント（ID: 1, 96）のみ利用可能です<br>
          • <strong>機能：</strong> 記事を MicroAd CMS に自動投稿します
        </div>
      </div>

      <!-- authentication_type-->
      <div class="row" style="margin-top: 30px">
        <label>{{ $t( "connections.authentication_type") }}:</label>
        <select class="form-select td-combo"
                v-model="modal_update.connection.authentication_type"
                @change="authentication_type_changed">
          <!-- Notion の場合、Application Passwords Authentication のみ表示 -->
          <template v-if="modal_update.connection.integration === 'notion'">
            <option>{{authentication_types[3]}}</option>
          </template>
          <template v-else>
            <option>{{authentication_types[0]}}</option>
            <option>{{authentication_types[1]}}</option>
            <option>{{authentication_types[2]}}</option>
            <option>{{authentication_types[3]}}</option>
          </template>
        </select>
      </div>

      <!-- domain_url: 0,1,2,3 -->
      <div class="row" style="margin-top: 30px">
        <label>{{ $t( "connections.domain_url") }}<span v-if="modal_update.connection.integration === 'notion'"> (Notion Database ID)</span>:</label>
        <input type="text" style="margin-left: 10px; width: 800px"
               v-model="modal_update.connection.domain_url"
               :placeholder="modal_update.connection.integration === 'notion' ? '例: 2a4d169ac66980b7b9d0e085aa715b80' : ''"/>
      </div>

      <!-- username: 0,1,3 (ただし Notion では username は不要) -->
      <div v-show="[
          authentication_types[0],
          authentication_types[1],
          authentication_types[3]
          ].includes( modal_update.connection.authentication_type) && modal_update.connection.integration !== 'notion'"
           class="row" style="margin-top: 30px">
        <label>{{ $t( "connections.username") }}:</label>
        <input type="text" style="margin-left: 10px; width: 800px"
               v-model="modal_update.connection.username"/>
      </div>

      <!-- password: 0,1 -->
      <div v-show="[
          authentication_types[0],
          authentication_types[1]
           ].includes( modal_update.connection.authentication_type)"
           class="row" style="margin-top: 30px">
        <label>{{ $t( "connections.password") }}:</label>
        <input type="text" style="margin-left: 10px; width: 800px"
               v-model="modal_update.connection.password"/>
      </div>

      <!-- client_id: 2 -->
      <div v-show="[
          authentication_types[2]
           ].includes( modal_update.connection.authentication_type)"
           class="row" style="margin-top: 30px">
        <label>Client ID:</label>
        <input type="text" style="margin-left: 10px; width: 800px"
               v-model="modal_update.connection.client_id"/>
      </div>

      <!-- client_secret: 2 -->
      <div v-show="[
          authentication_types[2]
           ].includes( modal_update.connection.authentication_type)"
           class="row" style="margin-top: 30px">
        <label>client secret:</label>
        <input type="text" style="margin-left: 10px; width: 800px"
               v-model="modal_update.connection.client_secret"/>
      </div>

      <!-- application_password: 3 -->
      <div v-show="[
          authentication_types[3]
           ].includes( modal_update.connection.authentication_type)"
          class="row" style="margin-top: 30px">
        <label>{{ $t( "connections.application_password") }}<span v-if="modal_update.connection.integration === 'notion'"> (Notion Integration Token)</span>:</label>
        <input type="text" style="margin-left: 10px; width: 800px"
               v-model="modal_update.connection.application_password"
               :placeholder="modal_update.connection.integration === 'notion' ? '例: secret_xxx または ntn_xxx' : ''"/>
      </div>

      <!-- 2 buttons-->
      <div class="row" style="margin-top: 20px">
        <button class="btn btn-primary"
                style="width: 120px; margin-left: 10px"
                @click="modal_update_confirm">
          {{ $t( "connections.update") }}</button>

        <button class="btn btn-primary" style="width: 120px; margin-left: 20px"
                @click="modal_update.show=false">
          {{ $t( "connections.return") }}</button>
      </div>

    </div>
  </div>
  <!-- end of Modal_new" -->

  <!-- modal_del_selected -->
  <div v-if="modal_del_selected.show" class="modal-del">
    <div class="modal-content">

      <div class="row" style="margin-top: 20px;text-align: center">
        <p>{{ modal_del_selected.msg }}</p>
      </div>

      <!-- 2 buttons -->

      <div class="row" style="margin-top: 20px;margin-left: 10px; margin-right: 10px">
        <button class="btn btn-primary"
                @click="del_selected_confirm">
          {{ $t( "connections.OK") }}</button>
      </div>

      <div class="row" style="margin-top: 20px;margin-left: 10px; margin-right: 10px">
        <button class="btn btn-primary"
                @click="modal_del_selected.show = false">
          {{ $t( "connections.cancel") }}</button>
      </div>

    </div>
  </div>
  <!-- end of modal_del_selected -->

  <!-- modal_del_1 -->
  <div v-if="modal_del_1.show" class="modal-del">
    <div class="modal-content">

      <div class="row" style="margin-top: 20px;text-align: center">
        <p>{{ modal_del_1.msg }}</p>
      </div>

      <!-- 2 buttons -->
      <div class="row" style="margin-top: 20px;margin-left: 10px; margin-right: 10px">
        <button class="btn btn-primary"
                @click="handleDelete_confirm">
          {{ $t( "connections.OK") }}</button>
      </div>

      <div class="row" style="margin-top: 20px;margin-left: 10px; margin-right: 10px">
        <button class="btn btn-primary"
                @click="modal_del_1.show = false">
          {{ $t( "connections.cancel") }}</button>
      </div>

    </div>
  </div>
  <!-- end of Modal_delete -->

  <div class="container">

    <!-- title-->
    <div class="row">
      <h4>{{ $t( "connections.title") }}</h4>
    </div>

    <!-- key for filer(關鍵詞過濾) -->
    <div class="row">
      <div class="column">

        <label for="search-keyword">{{ $t( "connections.search_keyword") }}:</label>
        <input type="text" id="search-keyword" v-model="filter.temp"
               style="padding: 0 10px; margin: 10px;"/>

        <button class="btn btn-primary" @click="handleFilter"
                style="padding: 0 50px; margin: 10px;">
          {{ $t( "connections.filter") }}</button>
      </div>

    </div>

    <!-- 3 button -->
    <div class="row">
      <div class="column" style="text-align: right">

        <button class="btn btn-primary"
                @click="modal_new_open"
                style="padding: 0 50px; margin: 10px;">
          + {{ $t( "connections.append") }}</button>

        <button class="btn btn-primary"
                @click="del_selected"
                style="padding: 0 50px; margin: 10px;">
          {{ $t( "connections.delete_much") }}</button>

        <button class="btn btn-primary" @click="refreshPage"
                style="padding: 0 50px; margin: 10px;">
          {{ $t( "connections.refresh") }}
        </button>

      </div>
    </div>

    <!-- select/de-select -->
    <div class="row">
      <div class="column">
        <input id="select_all" type="checkbox" @change="onSelectAllChange"/>
        <label style="padding: 0 10px; margin: 1px;" for="select_all">
          {{ $t( "connections.select_all") }}</label>
      </div>
    </div>

    <div class="row">

      <!-- the main table(主體表格) -->
      <table class="table table-hover">

        <thead>
        <tr>
          <th scope="col">{{ $t( "connections.selected") }}</th>

          <th scope="col">{{ $t( "connections.connection_name") }}</th>
          <th scope="col">{{ $t( "connections.author") }}</th>

          <th scope="col">{{ $t( "connections.operation") }}</th>
        </tr>
        </thead>

        <tbody>
        <tr v-for="(item, index) in filteredData" :key="item.id"
            v-show="( Math.floor( index / page.num) + 1) === page.current"
            style="vertical-align: center">

          <td>
            <input type="checkbox"
                   :disabled="item.article_list_id===data.public_id"
                   v-model="checked.items[ item.id]">
          </td>

          <td>
            {{item.connection_name}}
            <label v-show="item.article_list_id===data.public_id" class="custom-cell">
              {{ $t( "connections.default") }}</label>
          </td>

          <td>{{ datePipe(item.created_at, format = 'yyyy/MM/dd')}}</td>

          <td>
            <button class="btn btn-primary" style="padding: 0 20px"
                    :disabled="item.article_list_id===data.public_id"
                    @click="modal_update_open( item.id)">
              {{ $t( "connections.edit") }}</button>

            <button class="btn btn-danger"
                    style="padding: 0 20px; margin-top: 1px;margin-left: 10px"
                    :disabled="item.article_list_id===data.public_id"
                    @click="handleDelete( item.id, item.connection_name)">
              {{ $t( "connections.delete") }}</button>
          </td>

        </tr>
        </tbody>
      </table>

    </div>
    <div class="row">
      <paginate style="margin-left: 300px; cursor: pointer;"
                v-model="page.current"

                :page-count="page_count"
                :page-range="3"
                :margin-pages="2"

                :prev-text="'<'"
                :next-text="'>'"
      >
      </paginate>
    </div>
  </div>

</template>

<style scoped>
.custom-cell {
  text-align: center;
  margin-left: 15px;
  color: white;
  background-color: rgb(46,199,252);
  border-radius: 6px; /* 設定圓角半徑 */
  width: 100px;
}

.tr-line {
  height: 50px;
}

.td-left {
  text-align: right;
  width: 150px;
}

.td-right {
  text-align: left;
  width: 720px;
  //margin-right: 20px;
}

.td-combo {
  margin-top: 10px;
  margin-left: 10px;
  width: 320px;
}

.td-text {
  padding: 0 10px;
  margin: 10px;
  width: 720px;
}

.modal-del {
  height: 800px;
  max-height: 1024px;
  width: 480px;

  display: flex;
  justify-content: center;
  align-items: center;

  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0);
  z-index: 1000; /* 他の要素より前面に */

  top: 100px;
  left: 100px;
  position: absolute;
  padding: 20px;
}

.modal-create {
  height: 960px;
  max-height: 1280px;
  width: 960px;

  display: flex;
  justify-content: center;
  align-items: center;

  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0);
  z-index: 1000; /* 他の要素より前面に */

  top: 100px;
  left: 100px;
  position: absolute;
  padding: 20px;
}

.modal-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 5px;
  max-width: 1600px;  /* お好みに合わせて変更 */
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.tags {
  position: relative;
  float: left;
  color: #000000;
  line-height: 28px;

  margin-left: 10px;
  margin-top: 30px;
  margin-bottom: 1px;
  //margin: 0 4px 4px 0;
  padding: 0 22px 0 10px;
}

.tags .content {
  line-height: 28px;
}

.tags .del {
  width: 22px;
  height: 28px;
  text-align: center;
  cursor: pointer;
  position: absolute;
  top: -1px;
  right: 0;
}

.tags {
  background-color: #e0e0e0;
  border: 1px solid rgb(54, 54, 54);
  cursor: default;
}

</style>